import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface Breadcrumb {
  label: string;
  to?: string;
}

interface PageHeaderProps {
  title: string;
  description?: string;
  breadcrumbs?: Breadcrumb[];
  actions?: ReactNode;
  className?: string;
}

export function PageHeader({ title, description, breadcrumbs, actions, className }: PageHeaderProps) {
  // The company workspace (/company/:companyId/*) is the only tree whose
  // breadcrumb data still hardcodes the pre-migration '/company/...' shape
  // (241 call sites) — rather than touch every one of them, resolve the
  // companyId here, the single render site for every breadcrumb in the app.
  // Guarded to an exact '/company' | '/company/...' match (not a loose
  // startsWith('/company')) since PageHeader is also used by industry pages
  // that carry their own unrelated companyId param.
  const { companyId } = useParams<{ companyId?: string }>();
  const resolveTo = (to: string) => {
    if (!companyId) return to;
    if (to === '/company') return `/company/${companyId}`;
    if (to.startsWith('/company/')) return `/company/${companyId}${to.slice('/company'.length)}`;
    return to;
  };

  return (
    <div className={cn('flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8', className)}>
      <div>
        {breadcrumbs && breadcrumbs.length > 0 && (
          <nav className="flex items-center gap-1 text-xs text-gray-500 mb-2">
            {breadcrumbs.map((b, i) => (
              <span key={i} className="flex items-center gap-1">
                {b.to ? (
                  <Link to={resolveTo(b.to)} className="hover:text-blue-600">{b.label}</Link>
                ) : (
                  <span>{b.label}</span>
                )}
                {i < breadcrumbs.length - 1 && <ChevronRight className="w-3 h-3 text-gray-300" />}
              </span>
            ))}
          </nav>
        )}
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight dark:text-gray-100">{title}</h1>
        {description && <p className="text-gray-500 mt-1 dark:text-gray-400">{description}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}
